/**
 * Tabbed display panel for the Recipe Chef right side.
 * Tabs: Preview, Context, Prompt, HTML, Variables, Agent.
 */

import { useState, useMemo, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Eye, Bot, RefreshCw, FileDown, FileCode2, Code2, Braces, ScrollText, Layers, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const CodeMirror = lazy(() => import("@uiw/react-codemirror"));
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import type { DisplayBlock, HtmlPreviewBlock as HtmlPreviewBlockType } from "../../types/chatBlocks";
import type { ChefRecipeSummary } from "../../services/naraDoor";
import type { Document } from "../../services/naraDoor";
import { DocumentPreviewBlock } from "./DocumentPreviewBlock";
import { RecipeListBlock } from "./RecipeListBlock";
import { PromptContentBlock } from "./PromptContentBlock";
import { HtmlPreviewBlock } from "./HtmlPreviewBlock";
import { ChefContextTab } from "./ChefContextTab";

export type ChefTab = "preview" | "context" | "agent" | "prompt" | "html" | "variables";

interface ChefDisplayPanelProps {
  activeTab: ChefTab;
  onTabChange: (tab: ChefTab) => void;
  previewBlock: HtmlPreviewBlockType | null;
  agentBlocks: DisplayBlock[];
  recipeContext: ChefRecipeSummary | null;
  onSelectRecipe: (recipe: ChefRecipeSummary) => void;
  onSelectDocument: (doc: Document, projectId: string, projectName: string, workspaceId: string, workspaceName: string) => void;
  onSelectProject: (projectId: string, projectName: string, workspaceId: string, workspaceName: string) => void;
  selectedDocumentId?: string;
  selectedProjectId?: string;
  documentContextInfo?: {
    workspaceId: string;
    workspaceName: string;
    projectId: string;
    projectName: string;
    documentId: string;
  };
  onRefreshPreview?: () => void;
  isRefreshing?: boolean;
  onGeneratePdf?: () => void;
  pdfGenStatus?: { state: "idle" | "resolving" | "generating" | "done" | "error"; message?: string };
  /** AI generation prompt for the recipe */
  recipePrompt?: string;
  /** Recipe template source code (Jinja2/HTML) — PDF/Playwright template */
  recipeTemplateCode?: string;
  /** Recipe printable HTML template (Jinja2) — used for html_bundle exports */
  recipeTemplateHtml?: string;
  /** Parsed schema from the recipe (flat_fields, fields, arrays, example) */
  recipeSchema?: Record<string, unknown>;
  lastStoryPayload?: Record<string, unknown>;
  lastFiles?: Array<Record<string, unknown>>;
  /** Called when template code is edited */
  onTemplateCodeChange?: (code: string) => void;
  /** Called when printable HTML template is edited */
  onTemplateHtmlChange?: (html: string) => void;
  /** Called when prompt text is edited */
  onPromptChange?: (prompt: string) => void;
  /** Called when user clicks Publish */
  onPublish?: () => Promise<void>;
  isPublishing?: boolean;
  templateSaveStatus?: "idle" | "saving" | "saved" | "error";
  templateHtmlSaveStatus?: "idle" | "saving" | "saved" | "error";
  promptSaveStatus?: "idle" | "saving" | "saved" | "error";
  /** Recipe status: "draft" | "published" | "published_with_draft" */
  recipeStatus?: string;
  /** Which version the preview is showing */
  previewVersion?: "draft" | "published";
  onPreviewVersionChange?: (v: "draft" | "published") => void;
}

function AgentBlockRenderer({ block, recipeContext, onSelectRecipe }: {
  block: DisplayBlock;
  recipeContext: ChefRecipeSummary | null;
  onSelectRecipe: (recipe: ChefRecipeSummary) => void;
}) {
  switch (block.type) {
    case "document-preview":
      return <DocumentPreviewBlock block={block} expanded />;
    case "recipe-list":
      return (
        <RecipeListBlock
          recipes={block.recipes}
          onSelectRecipe={onSelectRecipe}
          selectedRecipeId={recipeContext?.id}
          expanded
        />
      );
    case "prompt-content":
      return <PromptContentBlock block={block} expanded />;
    default:
      return null;
  }
}

export function ChefDisplayPanel({
  activeTab,
  onTabChange,
  previewBlock,
  agentBlocks,
  recipeContext,
  onSelectRecipe,
  onSelectDocument,
  onSelectProject,
  selectedDocumentId,
  selectedProjectId,
  documentContextInfo,
  onRefreshPreview,
  isRefreshing,
  onGeneratePdf,
  pdfGenStatus,
  recipePrompt,
  recipeTemplateCode,
  recipeTemplateHtml,
  recipeSchema,
  lastStoryPayload,
  lastFiles,
  onTemplateCodeChange,
  onTemplateHtmlChange,
  onPromptChange,
  onPublish,
  isPublishing,
  templateSaveStatus,
  templateHtmlSaveStatus,
  promptSaveStatus,
  recipeStatus,
  previewVersion,
  onPreviewVersionChange,
}: ChefDisplayPanelProps) {
  const { t } = useTranslation();
  const latestAgentBlock = agentBlocks.length > 0 ? agentBlocks[agentBlocks.length - 1] : null;

  // Schema variable analysis (reused from NaraShapeTemplate.tsx)
  const schemaVariables = useMemo(() => {
    if (!recipeSchema) return null;

    const SYSTEM_DATA_FIELDS = new Set([
      "metadata", "sections", "story_payload", "files", "file_map",
      "custom_data", "name", "workspace_name", "project_name",
      "created_at", "updated_at", "created_by", "created_by_name",
      "ungrouped_images",
    ]);

    const flatFields: string[] = (recipeSchema as any).flat_fields || [];
    const promptText = recipePrompt || "";
    const promptLower = promptText.toLowerCase();

    const generated: { path: string; inPrompt: boolean }[] = [];
    const system: string[] = [];

    for (const field of flatFields) {
      const path = field.replace(/^data\./, "");
      const topLevelKey = path.split(".")[0].replace(/\[\]$/, "");

      if (SYSTEM_DATA_FIELDS.has(topLevelKey)) {
        system.push(path);
        continue;
      }

      const parts = path.split(".").map((p: string) => p.replace(/\[\]$/, ""));
      const lastPart = parts[parts.length - 1];
      const variations = [
        lastPart.toLowerCase(),
        lastPart.replace(/_/g, " ").toLowerCase(),
        lastPart.replace(/_/g, "").toLowerCase(),
      ];
      const inPrompt = variations.some((v) => promptLower.includes(v));

      generated.push({ path, inPrompt });
    }

    return { generated, system };
  }, [recipeSchema, recipePrompt]);

  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => onTabChange(v as ChefTab)}
      className="flex flex-col h-full"
    >
      <div className="px-3 pt-2 shrink-0">
        <TabsList className="w-full">
          <TabsTrigger value="agent" className="flex-1 gap-1">
            <Bot className="h-3.5 w-3.5" />
            Agent
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex-1 gap-1">
            <Eye className="h-3.5 w-3.5" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="context" className="flex-1 gap-1">
            <Layers className="h-3.5 w-3.5" />
            Context
          </TabsTrigger>
          <TabsTrigger value="html" className="flex-1 gap-1">
            <FileCode2 className="h-3.5 w-3.5" />
            HTML
          </TabsTrigger>
          <TabsTrigger value="variables" className="flex-1 gap-1">
            <Braces className="h-3.5 w-3.5" />
            Vars
          </TabsTrigger>
          <TabsTrigger value="prompt" className="flex-1 gap-1">
            <ScrollText className="h-3.5 w-3.5" />
            Prompt
          </TabsTrigger>
        </TabsList>
      </div>

      {/* Context — recipe + content side by side */}
      <TabsContent value="context" className="flex-1 h-0 overflow-hidden mt-0 p-4">
        <ChefContextTab
          onSelectRecipe={onSelectRecipe}
          selectedRecipeId={recipeContext?.id}
          onSelectDocument={onSelectDocument}
          onSelectProject={onSelectProject}
          selectedDocumentId={selectedDocumentId}
          selectedProjectId={selectedProjectId}
          currentContext={documentContextInfo}
        />
      </TabsContent>

      {/* Preview */}
      <TabsContent value="preview" className="flex-1 h-0 overflow-auto mt-0 p-4">
        <div className="flex flex-col h-full">
          {(onRefreshPreview || onGeneratePdf) && (
            <div className="flex justify-end gap-1.5 mb-2 shrink-0">
              {onGeneratePdf && (
                <button
                  onClick={onGeneratePdf}
                  disabled={pdfGenStatus?.state === "resolving" || pdfGenStatus?.state === "generating"}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {pdfGenStatus?.state === "resolving" || pdfGenStatus?.state === "generating" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FileDown className="h-3.5 w-3.5" />
                  )}
                  Generate PDF
                </button>
              )}
              {onRefreshPreview && (
                <button
                  onClick={onRefreshPreview}
                  disabled={isRefreshing}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
                  Refresh
                </button>
              )}
            </div>
          )}

          {/* Draft / Published toggle + Publish button */}
          {recipeContext && (
            <div className="flex items-center gap-2 mb-2 shrink-0">
              {onPreviewVersionChange && (
                <div className="flex rounded-lg border border-border overflow-hidden">
                  <button
                    onClick={() => onPreviewVersionChange("draft")}
                    className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                      previewVersion === "draft"
                        ? "bg-blue-600 text-white"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    Draft
                  </button>
                  <button
                    onClick={() => onPreviewVersionChange("published")}
                    className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                      previewVersion === "published"
                        ? "bg-blue-600 text-white"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    Published
                  </button>
                </div>
              )}

              {/* Status badge */}
              {recipeStatus && (
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  recipeStatus === "published"
                    ? "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300"
                    : recipeStatus === "published_with_draft"
                      ? "bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                }`}>
                  {recipeStatus === "published" ? "Published" : recipeStatus === "published_with_draft" ? "Draft changes" : "Draft"}
                </span>
              )}

              {onPublish && (
                <button
                  onClick={onPublish}
                  disabled={isPublishing || recipeStatus === "published"}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 ml-auto"
                >
                  {isPublishing ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  )}
                  Publish
                </button>
              )}
            </div>
          )}

          {/* PDF generation status banner */}
          {pdfGenStatus && pdfGenStatus.state !== "idle" && (
            <div className={`flex items-center gap-2.5 px-4 py-3 mb-3 rounded-lg text-sm shrink-0 ${
              pdfGenStatus.state === "error"
                ? "bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
                : pdfGenStatus.state === "done"
                  ? "bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300"
                  : "bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300"
            }`}>
              {(pdfGenStatus.state === "resolving" || pdfGenStatus.state === "generating") && (
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              )}
              {pdfGenStatus.state === "done" && <CheckCircle2 className="h-4 w-4 shrink-0" />}
              {pdfGenStatus.state === "error" && <AlertCircle className="h-4 w-4 shrink-0" />}
              <span>{pdfGenStatus.message}</span>
            </div>
          )}

          {previewBlock ? (
            <div className="flex-1 min-h-0">
              <HtmlPreviewBlock block={previewBlock} expanded />
            </div>
          ) : (
            <EmptyTabState icon={Eye} message="No preview yet" hint="Select a recipe and content, then preview" />
          )}
        </div>
      </TabsContent>

      {/* Prompt — AI generation prompt */}
      <TabsContent value="prompt" className="flex-1 h-0 overflow-hidden mt-0">
        <div className="flex flex-col h-full">
          {recipePrompt || onPromptChange ? (
            <CodeViewer
              code={recipePrompt || ""}
              language="text"
              label={`${recipeContext?.name ?? "Recipe"} — Prompt`}
              editable={!!onPromptChange}
              onChange={onPromptChange}
              saveStatus={promptSaveStatus}
            />
          ) : (
            <EmptyTabState icon={ScrollText} message="No recipe selected" hint="Select a recipe to view its AI prompt" />
          )}
        </div>
      </TabsContent>

      {/* HTML — template code + rendered output */}
      <TabsContent value="html" className="flex-1 h-0 overflow-hidden mt-0">
        <div className="flex flex-col h-full">
          {recipeTemplateCode || recipeTemplateHtml || previewBlock?.html ? (
            <div className="flex flex-col h-full">
              {/* Sub-tabs for template vs rendered */}
              <HtmlSubTabs
                templateCode={recipeTemplateCode}
                templateHtml={recipeTemplateHtml}
                renderedHtml={previewBlock?.html}
                templateLabel={recipeContext?.name ?? "Template"}
                renderedLabel={previewBlock?.recipe_name ?? "Rendered"}
                editable={!!onTemplateCodeChange}
                onTemplateChange={onTemplateCodeChange}
                onTemplateHtmlChange={onTemplateHtmlChange}
                templateSaveStatus={templateSaveStatus}
                templateHtmlSaveStatus={templateHtmlSaveStatus}
              />
            </div>
          ) : (
            <EmptyTabState icon={FileCode2} message="No HTML available" hint="Select a recipe or render a preview" />
          )}
        </div>
      </TabsContent>

      {/* Variables — Template Variables panel */}
      <TabsContent value="variables" className="flex-1 h-0 overflow-auto mt-0 p-4">
        {schemaVariables ? (
          <div className="space-y-4">
            {/* Generated Variables */}
            {schemaVariables.generated.length > 0 ? (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  {t("naraShape.detail.schema.generatedVars")}
                  {(() => {
                    const uncoveredCount = schemaVariables.generated.filter((v) => !v.inPrompt).length;
                    return uncoveredCount > 0 ? (
                      <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300">
                        {t("naraShape.detail.schema.uncoveredCount", { count: uncoveredCount })}
                      </span>
                    ) : null;
                  })()}
                </h4>
                <div className="space-y-1">
                  {schemaVariables.generated.map((v) => (
                    <div
                      key={v.path}
                      className="flex items-center gap-2 px-3 py-1.5 rounded text-sm font-mono"
                    >
                      <span className={v.inPrompt ? "text-green-600 dark:text-green-400" : "text-amber-500 dark:text-amber-400"}>
                        {v.inPrompt ? "\u2713" : "?"}
                      </span>
                      <span className="text-foreground">{v.path}</span>
                      {!v.inPrompt && (
                        <span className="text-xs text-muted-foreground ml-auto">
                          {t("naraShape.detail.schema.notInPrompt")}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("naraShape.detail.schema.noGeneratedVars")}
              </p>
            )}

            {/* System Variables (collapsed) */}
            {schemaVariables.system.length > 0 && (
              <details className="text-sm">
                <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                  {t("naraShape.detail.schema.systemVars", { count: schemaVariables.system.length })}
                </summary>
                <div className="mt-2 space-y-1 pl-2">
                  {schemaVariables.system.map((path) => (
                    <div key={path} className="text-xs font-mono text-muted-foreground px-3 py-1">
                      {path}
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Raw Schema (collapsed) */}
            <details className="text-sm">
              <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                {t("naraShape.detail.schema.rawSchema")}
              </summary>
              <div className="mt-2 bg-muted rounded-lg p-4">
                <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                  {JSON.stringify(recipeSchema, null, 2)}
                </pre>
              </div>
            </details>
          </div>
        ) : (
          <EmptyTabState icon={Braces} message={t("naraShape.detail.schema.noSchema")} hint="Select a recipe to view template variables" />
        )}
      </TabsContent>

      {/* Agent */}
      <TabsContent value="agent" className="flex-1 h-0 overflow-hidden mt-0">
        <div className="flex flex-col h-full p-4">
          {latestAgentBlock ? (
            <div className="flex-1 min-h-0 flex flex-col overflow-auto">
              <AgentBlockRenderer
                block={latestAgentBlock}
                recipeContext={recipeContext}
                onSelectRecipe={onSelectRecipe}
              />
            </div>
          ) : (
            <EmptyTabState icon={Bot} message="No agent output yet" hint="Agent-generated content will appear here" />
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
}

/* ── Shared helpers ───────────────────────────────────────────────── */

function SaveStatusIndicator({ status }: { status: "saving" | "saved" | "error" }) {
  return (
    <span className={`flex items-center gap-1 text-xs ${
      status === "saving" ? "text-blue-500" :
      status === "saved" ? "text-green-500" :
      "text-red-500"
    }`}>
      {status === "saving" && <Loader2 className="h-3 w-3 animate-spin" />}
      {status === "saved" && <CheckCircle2 className="h-3 w-3" />}
      {status === "error" && <AlertCircle className="h-3 w-3" />}
      {status === "saving" ? "Saving\u2026" : status === "saved" ? "Saved" : "Error"}
    </span>
  );
}

function EmptyTabState({ icon: Icon, message, hint }: {
  icon: React.ComponentType<{ className?: string }>;
  message: string;
  hint?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
      <Icon className="h-10 w-10 mb-3 opacity-30" />
      <p className="text-sm">{message}</p>
      {hint && <p className="text-xs mt-1 opacity-60">{hint}</p>}
    </div>
  );
}

function CodeViewer({ code, language, label, editable, onChange, saveStatus }: {
  code: string;
  language: string;
  label: string;
  editable?: boolean;
  onChange?: (value: string) => void;
  saveStatus?: "idle" | "saving" | "saved" | "error";
}) {
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
  };

  const [cmModules, setCmModules] = useState<{
    html: typeof import("@codemirror/lang-html");
    theme: typeof import("@uiw/codemirror-theme-tokyo-night");
    view: typeof import("@codemirror/view");
  } | null>(null);

  // Load CodeMirror extensions on first render
  useMemo(() => {
    if (!cmModules) {
      Promise.all([
        import("@codemirror/lang-html"),
        import("@uiw/codemirror-theme-tokyo-night"),
        import("@codemirror/view"),
      ]).then(([htmlMod, themeMod, viewMod]) => {
        setCmModules({ html: htmlMod, theme: themeMod, view: viewMod });
      });
    }
  }, []);

  const extensions = useMemo(() => {
    if (!cmModules) return [];
    const exts = [
      cmModules.view.EditorView.lineWrapping,
      cmModules.view.EditorView.contentAttributes.of({ style: "max-width: 120ch" }),
    ];
    if (language === "html" || language === "jinja2") {
      exts.push(cmModules.html.html());
    }
    return exts;
  }, [language, cmModules]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/50 shrink-0">
        <Code2 className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-foreground truncate">{label}</span>
        <span className="text-xs text-muted-foreground ml-auto">{language}</span>
        {saveStatus && saveStatus !== "idle" && (
          <SaveStatusIndicator status={saveStatus} />
        )}
        <button
          onClick={handleCopy}
          className="px-2 py-0.5 text-xs rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          Copy
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto [&_.cm-editor]:h-full [&_.cm-scroller]:overflow-auto">
        {cmModules ? (
          <Suspense fallback={<div className="p-4 text-xs text-muted-foreground">Loading editor...</div>}>
            <CodeMirror
              value={code}
              theme={cmModules.theme.tokyoNight}
              extensions={extensions}
              editable={editable ?? false}
              onChange={onChange}
              basicSetup={{
                lineNumbers: false,
                foldGutter: true,
                highlightActiveLine: editable ?? false,
                highlightSelectionMatches: true,
              }}
              className="h-full text-xs"
            />
          </Suspense>
        ) : (
          <pre className="flex-1 overflow-auto p-4 text-xs leading-relaxed font-mono bg-[#1e1e2e] text-[#cdd6f4] m-0">
            <code>{code}</code>
          </pre>
        )}
      </div>
    </div>
  );
}

function HtmlSubTabs({ templateCode, templateHtml, renderedHtml, templateLabel, renderedLabel, editable, onTemplateChange, onTemplateHtmlChange, templateSaveStatus, templateHtmlSaveStatus }: {
  templateCode?: string;
  templateHtml?: string;
  renderedHtml?: string;
  templateLabel: string;
  renderedLabel: string;
  editable?: boolean;
  onTemplateChange?: (code: string) => void;
  onTemplateHtmlChange?: (html: string) => void;
  templateSaveStatus?: "idle" | "saving" | "saved" | "error";
  templateHtmlSaveStatus?: "idle" | "saving" | "saved" | "error";
}) {
  const [templateVariant, setTemplateVariant] = useState<"pdf" | "printable">("pdf");
  const [view, setView] = useState<"template" | "rendered">(templateCode || templateHtml ? "template" : "rendered");

  const activeCode = templateVariant === "pdf" ? templateCode : templateHtml;
  const activeSaveStatus = templateVariant === "pdf" ? templateSaveStatus : templateHtmlSaveStatus;
  const activeOnChange = templateVariant === "pdf" ? onTemplateChange : onTemplateHtmlChange;

  return (
    <>
      {/* Row 1: PDF template / Printable HTML variant toggle */}
      <div className="flex items-center gap-1 px-4 py-2 border-b border-border bg-muted/30 shrink-0">
        <div className="flex rounded-lg border border-border overflow-hidden">
          <button
            onClick={() => setTemplateVariant("pdf")}
            className={`px-2.5 py-1 text-xs font-medium transition-colors ${
              templateVariant === "pdf" ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground hover:bg-muted"
            }`}
          >
            PDF template
          </button>
          <button
            onClick={() => setTemplateVariant("printable")}
            className={`px-2.5 py-1 text-xs font-medium transition-colors ${
              templateVariant === "printable" ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground hover:bg-muted"
            }`}
          >
            Printable HTML
          </button>
        </div>
      </div>

      {/* Row 2: Template / Rendered sub-toggle */}
      <div className="flex gap-1 px-4 py-2 border-b border-border bg-muted/20 shrink-0">
        {(templateVariant === "pdf" ? templateCode : templateHtml) !== undefined && (
          <button
            onClick={() => setView("template")}
            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              view === "template" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Template
          </button>
        )}
        {renderedHtml && (
          <button
            onClick={() => setView("rendered")}
            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              view === "rendered" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Rendered
          </button>
        )}
      </div>

      {/* Empty hint for printable HTML when unset */}
      {templateVariant === "printable" && !templateHtml && view === "template" && (
        <div className="px-4 py-2 text-xs text-muted-foreground bg-amber-50 dark:bg-amber-950/30 border-b border-border shrink-0">
          Empty — <code className="font-mono">html_bundle</code> exports will fall back to the PDF template.
        </div>
      )}

      <div className="flex-1 min-h-0">
        {view === "template" ? (
          <CodeViewer
            code={activeCode ?? ""}
            language="jinja2"
            label={templateVariant === "pdf" ? templateLabel : `${templateLabel} — Printable HTML`}
            editable={editable}
            onChange={activeOnChange}
            saveStatus={activeSaveStatus}
          />
        ) : renderedHtml ? (
          <CodeViewer code={renderedHtml} language="html" label={renderedLabel} />
        ) : null}
      </div>
    </>
  );
}
